import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Google Identity Services (GSI) global; no type package installed for it.
declare var google: any;

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';

export interface GoogleCredentialResponse {
  credential?: string;
}

/**
 * Single owner of the Google Identity Services client.
 *
 * FedCM only allows one outstanding `navigator.credentials.get()` request per page, so the script
 * load and the `initialize()` call have to be funnelled through here. Two competing callers produce
 * "Only one navigator.credentials.get request may be outstanding at one time" followed by
 * "signal is aborted without reason" when one of them calls `cancel()` on the other's request -
 * which is why `@abacritt/angularx-social-login`'s GoogleLoginProvider is no longer registered in
 * SharedModule and nothing outside this service may touch `google.accounts.id`.
 *
 * Sign-in is button-only; see cancelOneTap() for why One Tap is not used.
 */
@Injectable({
  providedIn: 'root'
})
export class GoogleCliService {

  /** Shared by every caller so the GSI client script is only ever added once. */
  private scriptPromise: Promise<void> | null = null;

  /** `google.accounts.id.initialize()` must run exactly once per page. */
  private initialized = false;

  /** Re-targeted every time a login form is shown, so the single GSI callback always reaches the live component. */
  private credentialHandler: ((response: GoogleCredentialResponse) => void) | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  loadGoogleScript(): Promise<void> {
    // Skip during server-side rendering
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      if (typeof google !== 'undefined' && google?.accounts?.id) {
        resolve();
        return;
      }

      // Another loader may already have appended the tag - wait on it instead of adding a second one.
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_GSI_SRC}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', (error) => reject(error));
        return;
      }

      const script = document.createElement('script');
      script.src = GOOGLE_GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);

      document.head.appendChild(script);
    });

    // Never cache a rejection, otherwise a transient network failure permanently breaks Google sign-in.
    this.scriptPromise.catch(() => {
      this.scriptPromise = null;
    });

    return this.scriptPromise;
  }

  /**
   * Loads the client and registers the handler that receives the Google credential.
   * Safe to call every time a login form opens - `initialize()` only ever runs once.
   */
  setupSignIn(handler: (response: GoogleCredentialResponse) => void): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    this.credentialHandler = handler;

    return this.loadGoogleScript().then(() => {
      if (this.initialized) {
        return;
      }

      google.accounts.id.initialize({
        client_id: environment.googleClientId,
        callback: (response: GoogleCredentialResponse) => this.credentialHandler?.(response),
        use_fedcm_for_prompt: true, // Enable FedCM explicitly
      });
      this.initialized = true;
    });
  }

  /** Renders the "Sign in with Google" button into the given container. */
  renderButton(container: HTMLElement): void {
    if (!isPlatformBrowser(this.platformId) || !this.initialized) {
      return;
    }

    container.innerHTML = ''; // Clear previous content
    google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      type: 'standard',
      shape: 'rectangular',
      text: 'signin_with',
    });
    container.style.visibility = 'visible';
  }

  /**
   * DO NOT add a `google.accounts.id.prompt()` call here or anywhere else.
   *
   * One Tap makes the client probe GET https://accounts.google.com/gsi/status, a cross-site request
   * that needs third-party cookies and an active Google session. Where the browser withholds those
   * (Chrome's third-party-cookie restrictions) Google answers 403 Forbidden and the client logs
   * "Check credential status returns invalid response". That is Google's endpoint refusing the call -
   * it cannot be fixed from application code or by widening the CSP.
   *
   * Sign-in therefore goes through renderButton() only, which needs no status probe. The FedCM
   * moment-notification methods (`isNotDisplayed`, `isSkippedMoment`, ...) are deprecated as well, so
   * there is no supported way to observe One Tap quietly.
   * https://developers.google.com/identity/gsi/web/guides/fedcm-migration
   */
  cancelOneTap(): void {
    if (!isPlatformBrowser(this.platformId) || !this.initialized) {
      return;
    }

    // Closes any GSI UI Google may have opened and detaches the handler, so a destroyed login form
    // can never receive a credential.
    google.accounts.id.cancel();
    this.credentialHandler = null;
  }
}
